import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, ClipboardCheck, Pencil, Plus, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { DatePicker } from '@/components/ui/DatePicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { MobileListCard, MobileListCardStack } from '@/components/ui/MobileListCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Switch } from '@/components/ui/Switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Textarea } from '@/components/ui/Textarea';
import { RemoveIconButton, SummaryCard } from '@/components/equipment/DetailRows';
import { DueDate } from '@/components/equipment/EquipmentBadges';
import {
  useDeleteMaintenanceLog,
  useDeleteMaintenancePlan,
  useMaintenanceLogs,
  useMaintenancePlans,
  useSaveMaintenanceLog,
  useSaveMaintenancePlan,
  type Equipment,
  type MaintenanceLog,
  type MaintenancePlan,
} from '@/features/equipment/hooks';
import {
  MAINTENANCE_INTERVAL_UNITS,
  MAINTENANCE_KINDS,
  MAINTENANCE_KIND_META,
  dueState,
  formatInterval,
  getMaintenanceKindMeta,
  nextDueFrom,
  type MaintenanceIntervalUnit,
  type MaintenanceKind,
} from '@/features/equipment/model';
import {
  centsToInputDollars,
  formatCurrency,
  formatDateOnly,
  inputDollarsToCents,
  todayIsoDate,
} from '@/lib/formatters';
import { useIsMobile } from '@/lib/useMediaQuery';

const NO_PLAN = '__none';
const LOG_YEARS = { past: 30, future: 1 };
const PLAN_YEARS = { past: 10, future: 10 };

type LogDialogState =
  | { mode: 'new'; plan: MaintenancePlan | null }
  | { mode: 'edit'; log: MaintenanceLog }
  | null;

type PlanDialogState = { mode: 'new' } | { mode: 'edit'; plan: MaintenancePlan } | null;

export function EquipmentMaintenanceTab({
  equipment,
  canLog,
}: {
  equipment: Equipment;
  canLog: boolean;
}) {
  const isMobile = useIsMobile();
  const { data: plans, isLoading: plansLoading } = useMaintenancePlans(equipment.id);
  const { data: logs, isLoading: logsLoading } = useMaintenanceLogs(equipment.id);
  const deletePlan = useDeleteMaintenancePlan();
  const deleteLog = useDeleteMaintenanceLog();
  const [planDialog, setPlanDialog] = useState<PlanDialogState>(null);
  const [logDialog, setLogDialog] = useState<LogDialogState>(null);

  const planById = useMemo(() => new Map((plans ?? []).map((p) => [p.id, p])), [plans]);

  const stats = useMemo(() => {
    const year = todayIsoDate().slice(0, 4);
    let spendYear = 0;
    let spendAll = 0;
    let downtimeYear = 0;
    let downtimeAll = 0;
    for (const l of logs ?? []) {
      spendAll += l.cost_cents ?? 0;
      downtimeAll += l.downtime_hours ?? 0;
      if (l.performed_on.startsWith(year)) {
        spendYear += l.cost_cents ?? 0;
        downtimeYear += l.downtime_hours ?? 0;
      }
    }
    const active = (plans ?? []).filter((p) => p.is_active);
    const nextDue = active
      .map((p) => p.next_due_on)
      .filter((d): d is string => !!d)
      .sort()[0];
    const overdue = active.filter((p) => dueState(p.next_due_on) === 'overdue').length;
    return { year, spendYear, spendAll, downtimeYear, downtimeAll, nextDue, overdue };
  }, [logs, plans]);

  const removePlan = async (plan: MaintenancePlan) => {
    if (
      !window.confirm(
        `Delete the “${plan.title}” schedule? Past log entries stay, unlinked from the schedule.`,
      )
    ) {
      return;
    }
    try {
      await deletePlan.mutateAsync({ id: plan.id, equipmentId: equipment.id });
      toast.success('Schedule deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete schedule');
    }
  };

  const removeLog = async (log: MaintenanceLog) => {
    if (!window.confirm(`Delete the ${formatDateOnly(log.performed_on)} entry “${log.summary}”?`)) {
      return;
    }
    try {
      await deleteLog.mutateAsync({ id: log.id, equipmentId: equipment.id });
      toast.success('Entry deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete entry');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard
          label="Next due"
          value={stats.nextDue ? <DueDate date={stats.nextDue} className="flex-wrap text-xl" /> : '—'}
          caption={stats.nextDue ? undefined : 'No active schedule'}
        />
        <SummaryCard
          label="Overdue schedules"
          value={stats.overdue.toLocaleString()}
          tone={stats.overdue > 0 ? 'warning' : 'neutral'}
        />
        <SummaryCard
          label={`Spend ${stats.year}`}
          value={formatCurrency(stats.spendYear, { whole: true })}
          caption={`${formatCurrency(stats.spendAll, { whole: true })} all time`}
        />
        <SummaryCard
          label={`Downtime ${stats.year}`}
          value={`${formatHours(stats.downtimeYear)} h`}
          caption={`${formatHours(stats.downtimeAll)} h all time`}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>Preventive maintenance schedules</CardTitle>
            {canLog && (
              <Button size="sm" variant="outline" onClick={() => setPlanDialog({ mode: 'new' })}>
                <Plus className="h-3.5 w-3.5" />
                Add schedule
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {plansLoading ? (
            <SkeletonRows />
          ) : !plans || plans.length === 0 ? (
            <EmptyState
              icon={CalendarClock}
              title="No recurring maintenance scheduled"
              action={
                canLog ? (
                  <Button variant="outline" onClick={() => setPlanDialog({ mode: 'new' })}>
                    <Plus className="h-4 w-4" />
                    Add schedule
                  </Button>
                ) : undefined
              }
            />
          ) : isMobile ? (
            <MobileListCardStack>
              {plans.map((plan) => (
                <MobileListCard
                  key={plan.id}
                  title={plan.title}
                  subtitle={formatInterval(plan.interval_value, plan.interval_unit)}
                  dimmed={!plan.is_active}
                  badges={
                    !plan.is_active ? (
                      <Badge variant="muted">Paused</Badge>
                    ) : dueState(plan.next_due_on) === 'overdue' ? (
                      <Badge variant="warning">Overdue</Badge>
                    ) : dueState(plan.next_due_on) === 'due_soon' ? (
                      <Badge variant="info-soft">Due soon</Badge>
                    ) : undefined
                  }
                  meta={[
                    { label: 'Next due', value: formatDateOnly(plan.next_due_on) },
                    { label: 'Last done', value: formatDateOnly(plan.last_completed_on) },
                    ...(plan.responsible ? [{ label: 'Who', value: plan.responsible }] : []),
                  ]}
                  trailing={
                    canLog ? (
                      <>
                        {plan.is_active && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-11"
                            onClick={() => setLogDialog({ mode: 'new', plan })}
                          >
                            <ClipboardCheck className="h-3.5 w-3.5" />
                            Log work
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-11 w-11 text-muted-foreground"
                          onClick={() => setPlanDialog({ mode: 'edit', plan })}
                          aria-label={`Edit ${plan.title}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </>
                    ) : null
                  }
                />
              ))}
            </MobileListCardStack>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Interval</TableHead>
                  <TableHead>Last done</TableHead>
                  <TableHead>Next due</TableHead>
                  <TableHead>Responsible</TableHead>
                  <TableHead className="w-px" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.id} className={plan.is_active ? undefined : 'opacity-60'}>
                    <TableCell className="max-w-[22rem]">
                      <div className="font-medium">{plan.title}</div>
                      {plan.instructions && (
                        <div className="line-clamp-2 whitespace-pre-wrap text-xs text-muted-foreground">
                          {plan.instructions}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatInterval(plan.interval_value, plan.interval_unit)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {formatDateOnly(plan.last_completed_on)}
                    </TableCell>
                    <TableCell>
                      {plan.is_active ? (
                        <DueDate date={plan.next_due_on} />
                      ) : (
                        <Badge variant="muted">Paused</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{plan.responsible ?? '—'}</TableCell>
                    <TableCell>
                      {canLog && (
                        <div className="flex items-center justify-end gap-1">
                          {plan.is_active && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setLogDialog({ mode: 'new', plan })}
                            >
                              <ClipboardCheck className="h-3.5 w-3.5" />
                              Log work
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-9 w-9 text-muted-foreground"
                            onClick={() => setPlanDialog({ mode: 'edit', plan })}
                            aria-label={`Edit ${plan.title}`}
                            title="Edit schedule"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <RemoveIconButton
                            label={`Delete ${plan.title}`}
                            onClick={() => void removePlan(plan)}
                            disabled={deletePlan.isPending}
                          />
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>Maintenance log</CardTitle>
            {canLog && (
              <Button size="sm" onClick={() => setLogDialog({ mode: 'new', plan: null })}>
                <Plus className="h-3.5 w-3.5" />
                Log maintenance
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <SkeletonRows />
          ) : !logs || logs.length === 0 ? (
            <EmptyState icon={Wrench} title="No maintenance logged yet" />
          ) : isMobile ? (
            <MobileListCardStack>
              {logs.map((log) => {
                const kind = getMaintenanceKindMeta(log.kind);
                const plan = log.plan_id ? planById.get(log.plan_id) : null;
                return (
                  <MobileListCard
                    key={log.id}
                    title={log.summary}
                    subtitle={[formatDateOnly(log.performed_on), log.performed_by]
                      .filter(Boolean)
                      .join(' · ')}
                    badges={
                      <>
                        <Badge variant={kind.variant}>{kind.label}</Badge>
                        {plan && <Badge variant="outline">{plan.title}</Badge>}
                      </>
                    }
                    amount={
                      log.cost_cents != null
                        ? { value: formatCurrency(log.cost_cents), caption: 'Cost' }
                        : undefined
                    }
                    meta={
                      log.downtime_hours != null
                        ? [{ label: 'Downtime', value: `${formatHours(log.downtime_hours)} h` }]
                        : undefined
                    }
                    onTap={canLog ? () => setLogDialog({ mode: 'edit', log }) : undefined}
                    trailing={canLog ? undefined : null}
                  />
                );
              })}
            </MobileListCardStack>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Work done</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Downtime</TableHead>
                  <TableHead className="w-px" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => {
                  const kind = getMaintenanceKindMeta(log.kind);
                  const plan = log.plan_id ? planById.get(log.plan_id) : null;
                  return (
                    <TableRow key={log.id}>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {formatDateOnly(log.performed_on)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={kind.variant}>{kind.label}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[24rem]">
                        <div className="font-medium">{log.summary}</div>
                        {log.details && (
                          <div className="line-clamp-2 whitespace-pre-wrap text-xs text-muted-foreground">
                            {log.details}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{plan?.title ?? '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{log.performed_by ?? '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(log.cost_cents)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {log.downtime_hours == null ? '—' : `${formatHours(log.downtime_hours)} h`}
                      </TableCell>
                      <TableCell>
                        {canLog && (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 text-muted-foreground"
                              onClick={() => setLogDialog({ mode: 'edit', log })}
                              aria-label={`Edit ${log.summary}`}
                              title="Edit entry"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <RemoveIconButton
                              label={`Delete ${log.summary}`}
                              onClick={() => void removeLog(log)}
                              disabled={deleteLog.isPending}
                            />
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PlanDialog
        state={planDialog}
        equipmentId={equipment.id}
        onClose={() => setPlanDialog(null)}
        onDelete={(plan) => {
          setPlanDialog(null);
          void removePlan(plan);
        }}
      />
      <LogDialog
        state={logDialog}
        equipmentId={equipment.id}
        plans={plans ?? []}
        onClose={() => setLogDialog(null)}
        onDelete={(log) => {
          setLogDialog(null);
          void removeLog(log);
        }}
      />
    </div>
  );
}

function PlanDialog({
  state,
  equipmentId,
  onClose,
  onDelete,
}: {
  state: PlanDialogState;
  equipmentId: string;
  onClose: () => void;
  onDelete: (plan: MaintenancePlan) => void;
}) {
  const save = useSaveMaintenancePlan();
  const editing = state?.mode === 'edit' ? state.plan : null;
  const [title, setTitle] = useState('');
  const [intervalValue, setIntervalValue] = useState('3');
  const [intervalUnit, setIntervalUnit] = useState<MaintenanceIntervalUnit>('month');
  const [lastCompleted, setLastCompleted] = useState('');
  const [nextDue, setNextDue] = useState('');
  const [responsible, setResponsible] = useState('');
  const [instructions, setInstructions] = useState('');
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (!state) return;
    setTitle(editing?.title ?? '');
    setIntervalValue(String(editing?.interval_value ?? 3));
    setIntervalUnit(editing?.interval_unit ?? 'month');
    setLastCompleted(editing?.last_completed_on ?? '');
    setNextDue(editing?.next_due_on ?? '');
    setResponsible(editing?.responsible ?? '');
    setInstructions(editing?.instructions ?? '');
    setActive(editing?.is_active ?? true);
  }, [state, editing]);

  const parsedInterval = Number(intervalValue);
  const intervalValid =
    Number.isInteger(parsedInterval) && parsedInterval >= 1 && parsedInterval <= 1000;
  const suggestedNext = intervalValid
    ? nextDueFrom(lastCompleted || todayIsoDate(), parsedInterval, intervalUnit)
    : null;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!intervalValid) {
      toast.error('Interval must be a whole number between 1 and 1000.');
      return;
    }
    const intervalChanged =
      !!editing &&
      (editing.interval_value !== parsedInterval || editing.interval_unit !== intervalUnit);
    const nextUntouched = !!editing && (editing.next_due_on ?? '') === nextDue;
    try {
      await save.mutateAsync({
        id: editing?.id,
        equipmentId,
        values: {
          title,
          instructions,
          interval_value: parsedInterval,
          interval_unit: intervalUnit,
          last_completed_on: lastCompleted || null,
          next_due_on:
            intervalChanged && nextUntouched && !lastCompleted ? suggestedNext : nextDue || null,
          responsible,
          is_active: active,
        },
      });
      toast.success(editing ? 'Schedule updated' : 'Schedule added');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save schedule');
    }
  };

  return (
    <Dialog open={state !== null} onOpenChange={(open) => !open && !save.isPending && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit schedule' : 'Add maintenance schedule'}</DialogTitle>
            <DialogDescription>
              Logging work against a schedule moves its next due date forward automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="plan-title">
                Task <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <Input
                id="plan-title"
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Change hydraulic oil filter"
                maxLength={160}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan-interval">Every</Label>
              <div className="flex gap-2">
                <Input
                  id="plan-interval"
                  value={intervalValue}
                  onChange={(e) => setIntervalValue(e.target.value)}
                  inputMode="numeric"
                  className="w-20 tabular-nums"
                  aria-label="Interval"
                />
                <Select
                  value={intervalUnit}
                  onValueChange={(v) => setIntervalUnit(v as MaintenanceIntervalUnit)}
                >
                  <SelectTrigger aria-label="Interval unit" className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAINTENANCE_INTERVAL_UNITS.map((u) => (
                      <SelectItem key={u} value={u}>
                        {parsedInterval === 1 ? u : `${u}s`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan-responsible">Responsible</Label>
              <Input
                id="plan-responsible"
                value={responsible}
                onChange={(e) => setResponsible(e.target.value)}
                placeholder="Person, team or service vendor"
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan-last">Last done</Label>
              <DatePicker
                id="plan-last"
                value={lastCompleted}
                onChange={setLastCompleted}
                placeholder="Never / unknown"
                aria-label="Last done"
                showShortcuts={false}
                yearRange={PLAN_YEARS}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan-next">Next due</Label>
              <DatePicker
                id="plan-next"
                value={nextDue}
                onChange={setNextDue}
                placeholder={suggestedNext ? formatDateOnly(suggestedNext) : 'Pick a date'}
                aria-label="Next due"
                showShortcuts={false}
                yearRange={PLAN_YEARS}
              />
              {!nextDue && suggestedNext && (
                <p className="text-xs text-muted-foreground">
                  Leave blank to use {formatDateOnly(suggestedNext)}.
                </p>
              )}
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="plan-instructions">Instructions</Label>
              <Textarea
                id="plan-instructions"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Steps, parts and consumables, torque specs, reference to the manual page."
                rows={4}
                maxLength={4000}
              />
            </div>
            {editing && (
              <label className="flex min-h-11 items-center justify-between gap-3 rounded-md border border-border px-3 text-sm sm:col-span-2">
                <span>
                  Active
                  <span className="block text-xs text-muted-foreground">
                    Paused schedules don't count toward due or overdue.
                  </span>
                </span>
                <Switch checked={active} onCheckedChange={setActive} aria-label="Active" />
              </label>
            )}
          </div>
          <DialogFooter className="mt-6 sm:justify-between">
            {editing ? (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => onDelete(editing)}
                disabled={save.isPending}
              >
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="ghost" onClick={onClose} disabled={save.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : editing ? 'Save schedule' : 'Add schedule'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LogDialog({
  state,
  equipmentId,
  plans,
  onClose,
  onDelete,
}: {
  state: LogDialogState;
  equipmentId: string;
  plans: MaintenancePlan[];
  onClose: () => void;
  onDelete: (log: MaintenanceLog) => void;
}) {
  const save = useSaveMaintenanceLog();
  const editing = state?.mode === 'edit' ? state.log : null;
  const [performedOn, setPerformedOn] = useState(todayIsoDate());
  const [kind, setKind] = useState<MaintenanceKind>('preventive');
  const [planId, setPlanId] = useState(NO_PLAN);
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [performedBy, setPerformedBy] = useState('');
  const [cost, setCost] = useState('');
  const [downtime, setDowntime] = useState('');

  useEffect(() => {
    if (!state) return;
    if (state.mode === 'edit') {
      const l = state.log;
      setPerformedOn(l.performed_on);
      setKind(l.kind);
      setPlanId(l.plan_id ?? NO_PLAN);
      setSummary(l.summary);
      setDetails(l.details ?? '');
      setPerformedBy(l.performed_by ?? '');
      setCost(centsToInputDollars(l.cost_cents));
      setDowntime(l.downtime_hours == null ? '' : String(l.downtime_hours));
      return;
    }
    const plan = state.plan;
    setPerformedOn(todayIsoDate());
    setKind(plan ? 'preventive' : 'repair');
    setPlanId(plan?.id ?? NO_PLAN);
    setSummary(plan?.title ?? '');
    setDetails('');
    setPerformedBy(plan?.responsible ?? '');
    setCost('');
    setDowntime('');
  }, [state]);

  const selectablePlans = plans.filter((p) => p.is_active || p.id === planId);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let costCents: number | null;
    try {
      costCents = inputDollarsToCents(cost);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid cost.');
      return;
    }
    let downtimeHours: number | null = null;
    if (downtime.trim()) {
      const parsed = Number(downtime.trim());
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > 999_999) {
        toast.error('Downtime must be a number of hours, 0 or more.');
        return;
      }
      downtimeHours = Math.round(parsed * 100) / 100;
    }
    try {
      await save.mutateAsync({
        id: editing?.id,
        equipmentId,
        values: {
          plan_id: planId === NO_PLAN ? null : planId,
          performed_on: performedOn,
          kind,
          summary,
          details,
          performed_by: performedBy,
          cost_cents: costCents,
          downtime_hours: downtimeHours,
        },
      });
      toast.success(editing ? 'Entry updated' : 'Maintenance logged');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save entry');
    }
  };

  return (
    <Dialog open={state !== null} onOpenChange={(open) => !open && !save.isPending && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit log entry' : 'Log maintenance'}</DialogTitle>
          </DialogHeader>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="log-date">
                Date <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <DatePicker
                id="log-date"
                value={performedOn}
                onChange={(v) => setPerformedOn(v || todayIsoDate())}
                aria-label="Date performed"
                yearRange={LOG_YEARS}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="log-kind">Type</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as MaintenanceKind)}>
                <SelectTrigger id="log-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MAINTENANCE_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {MAINTENANCE_KIND_META[k].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectablePlans.length > 0 && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="log-plan">Schedule</Label>
                <Select
                  value={planId}
                  onValueChange={(v) => {
                    setPlanId(v);
                    const plan = plans.find((p) => p.id === v);
                    if (plan && !summary.trim()) setSummary(plan.title);
                    if (plan) setKind('preventive');
                  }}
                >
                  <SelectTrigger id="log-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_PLAN}>Unscheduled work</SelectItem>
                    {selectablePlans.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.title} · {formatInterval(p.interval_value, p.interval_unit).toLowerCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="log-summary">
                Work done <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <Input
                id="log-summary"
                autoFocus
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="e.g. Replaced spindle belt"
                maxLength={200}
                required
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="log-details">Details</Label>
              <Textarea
                id="log-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Findings, parts used, readings, follow-ups."
                rows={3}
                maxLength={4000}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="log-by">Performed by</Label>
              <Input
                id="log-by"
                value={performedBy}
                onChange={(e) => setPerformedBy(e.target.value)}
                placeholder="Technician or service company"
                maxLength={120}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="log-cost">Cost</Label>
              <Input
                id="log-cost"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
                className="tabular-nums"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="log-downtime">Downtime (hours)</Label>
              <Input
                id="log-downtime"
                value={downtime}
                onChange={(e) => setDowntime(e.target.value)}
                placeholder="0"
                inputMode="decimal"
                className="tabular-nums"
              />
            </div>
          </div>
          <DialogFooter className="mt-6 sm:justify-between">
            {editing ? (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => onDelete(editing)}
                disabled={save.isPending}
              >
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="ghost" onClick={onClose} disabled={save.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : editing ? 'Save entry' : 'Log it'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function formatHours(hours: number): string {
  return hours.toLocaleString('en-US', { maximumFractionDigits: 2 });
}
